import type { HafasDeparturesResponse } from '../types/HafasDeparturesResponse';
import type { HafasResolveResponse } from '../types/HafasResolveResponse';
import type { LineEntry } from '../types/LineEntry';
import type { ResolveResponse } from '../types/ResolveResponse';
import type { RouteQuery } from '../types/RouteQuery';
import type { RouteResponse } from '../types/RouteResponse';

/** Values `apiGet` will serialize as URL query params. */
export type QueryValue = string | number | boolean | null | undefined;

/**
 * Thrown by {@link apiGet} on a non-2xx response (`status` carries the HTTP
 * code) or on a 2xx body that fails to JSON-parse (`status` carries the 2xx).
 */
export class ApiError extends Error {
  /** HTTP status the response carried when the error was raised. */
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function buildUrl(path: string, query: Record<string, QueryValue> | undefined): string {
  const requestUrl = new URL(path, location.origin);
  if (!query) return requestUrl.href;
  for (const [parameterName, parameterValue] of Object.entries(query)) {
    if (parameterValue === null || parameterValue === undefined) continue;
    requestUrl.searchParams.set(parameterName, String(parameterValue));
  }
  return requestUrl.href;
}

/**
 * Extracts a human-readable message from a non-2xx response, preferring the
 * `error` field of a JSON body and falling back to `<status> <statusText>`.
 */
async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown } | null;
    if (body && typeof body.error === 'string' && body.error) return body.error;
  } catch {
    /* non-JSON or empty body — fall through */
  }
  return `${String(response.status)} ${response.statusText}`;
}

/**
 * GET `path` with optional query params, JSON-parse the body, cast to `T`.
 * Throws {@link ApiError} on non-2xx or unparseable JSON. Aborts propagate
 * as `DOMException(AbortError)`; network failures propagate as `TypeError`
 * — neither is wrapped. Runtime shape is not validated — callers should
 * validate with valibot before consuming.
 */
export async function apiGet<T>(
  path: string,
  query?: Record<string, QueryValue>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(buildUrl(path, query), { signal });
  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response));
  }
  try {
    return (await response.json()) as T;
  } catch (error: unknown) {
    throw new ApiError(response.status, `invalid JSON response: ${String(error)}`);
  }
}

/** Resolves a search query to matching stops + addresses. See also {@link resolveByStopId}. */
export function resolveByQuery(query: string, signal?: AbortSignal): Promise<ResolveResponse> {
  return apiGet('/v1/resolve', { q: query }, signal);
}

/** Resolves a specific `stop_id` to at most one stop result. See also {@link resolveByQuery}. */
export function resolveByStopId(stopId: number, signal?: AbortSignal): Promise<ResolveResponse> {
  return apiGet('/v1/resolve', { stop_id: stopId }, signal);
}

/** Fetches the catalog used by the exclude widget. */
export function getLines(signal?: AbortSignal): Promise<LineEntry[]> {
  return apiGet('/v1/lines', undefined, signal);
}

/**
 * Explicit param mapping for {@link RouteQuery} — keeps `apiGet` free of
 * unsafe wide casts and forces future `RouteQuery` fields (arrays, nested
 * objects) to be handled deliberately rather than `String()`-coerced.
 */
function routeQueryToParameters(query: RouteQuery): Record<string, QueryValue> {
  return {
    origin_lat: query.origin_lat,
    origin_lon: query.origin_lon,
    origin_stop_id: query.origin_stop_id,
    origin_address: query.origin_address,
    dest_lat: query.dest_lat,
    dest_lon: query.dest_lon,
    dest_stop_id: query.dest_stop_id,
    dest_address: query.dest_address,
    time: query.time,
    max_transfers: query.max_transfers,
    block_lines: query.block_lines,
    block_modes: query.block_modes,
  };
}

/** Runs a routing query and returns the top journeys + realtime freshness. */
export function getRoutes(query: RouteQuery, signal?: AbortSignal): Promise<RouteResponse> {
  return apiGet('/v1/routes', routeQueryToParameters(query), signal);
}

/** HAFAS live departures for the stop identified by `extId`. */
export function getHafasDepartures(
  extensionId: string,
  signal?: AbortSignal,
): Promise<HafasDeparturesResponse> {
  return apiGet('/v1/hafas-departures', { ext_id: extensionId }, signal);
}

/** HAFAS name-search for stops. */
export function getHafasResolve(
  query: string,
  signal?: AbortSignal,
): Promise<HafasResolveResponse> {
  return apiGet('/v1/hafas-resolve', { q: query }, signal);
}
